/**
 * SA-448 (spec 2026-09-22 §3): the process map's layout. Pure: reads nothing
 * but its arguments, so the same projection and column count always give the
 * same bytes (D5), and happy-dom can test the geometry (§7).
 */
import type { ProcessOverviewVM } from '@/utils/workflowOverview'
import {
  labelRect, MAP_GRID as G,
  type CardBox, type EdgeKind, type EdgePath, type LabelBox, type LaneBox, type MapLayout, type Point, type StubBlock, type StubLine,
} from './mapTypes'
import { estimateWidth, wrapText } from './textMetrics'
import { packIntervals, type Interval } from './packing'
import { placeCells, type Cell, type Placement } from './grid'

export interface RawEdge {
  key: string
  from: string
  to: string
  exit: string
  back: boolean
  kind: EdgeKind
  label: string | null
  order: string
}
export interface RawStub { step: string; lines: StubLine[]; order: string }

export function edgeWords(name: string): string {
  return name.replace(/_/g, ' ')
}

/** Spec §3.4 and D8: every step exit becomes an edge of one of four kinds, or a stub line. */
export function classify(vm: ProcessOverviewVM, placement: Placement): { edges: RawEdge[]; stubs: RawStub[] } {
  const edges: RawEdge[] = []
  const stubs: RawStub[] = []
  for (const step of vm.steps) {
    const s = placement.cells.get(step.id)!
    const order = String(step.number).padStart(3, '0')
    const stepExits = step.exits.filter((e) => e.targetKind === 'step' && e.target !== step.id && placement.cells.has(e.target))
    const lines: StubLine[] = []
    for (const e of step.exits) {
      const text = `${edgeWords(e.name)} → ${e.targetLabel}`
      if (e.targetKind !== 'step') {
        lines.push({ exit: e.name, kind: e.targetKind, text })
        continue
      }
      if (e.target === step.id) {
        lines.push({ exit: e.name, kind: 'self', text })
        continue
      }
      const t = placement.cells.get(e.target)
      if (!t) continue // D12: an exit to a step the document does not have draws nothing
      let kind: EdgeKind
      if (s.lane === t.lane && s.row === t.row) kind = t.col === s.col + s.dir ? 'straight' : 'arc'
      else if (s.lane === t.lane && t.index === s.index + 1 && t.row === s.row + 1) kind = 'drop'
      else kind = 'route'
      edges.push({
        key: `${step.id}:${e.name}`, from: step.id, to: e.target, exit: e.name, back: e.isBackEdge, kind,
        label: e.name === 'done' && stepExits.length === 1 && !e.isBackEdge ? null : edgeWords(e.name),
        order: `${order}:${e.name}`,
      })
    }
    if (lines.length) stubs.push({ step: step.id, lines, order })
  }
  return { edges, stubs }
}

const LABEL_MARGIN = 4
/** How far beside its line a label sits when it cannot be centred on one. */
const LABEL_OFFSET = 6
/** The clearance a label needs at each end of the segment it is centred on. */
const LABEL_CLEAR = 8
const ARROW_GAP = 2
const CORNER = 8
/** SA-460 (spec D7): an arc's shoulders. A loop reads as a lifted curve the
 * way the wireframe's quadratic does; bounded, like every corner, by half the
 * shorter adjacent segment, so a level-0 arc still fits its channel. Path
 * shape only — the points, the claimed interval and the label do not move. */
export const ARC_CORNER = 24
const BAND_REACH = 6
const BAND_GAP = 12

type Side = 'top' | 'bottom' | 'left' | 'right'
interface Attachment { key: string; partnerX: number }
interface Run { channel: number; x1: number; x2: number }

const fmt = (n: number) => Math.round(n * 100) / 100

/** SA-454: the left margin is the lane labels' column. Spec §3.4 already keeps
 * every LINE out of the margins, but a label centred on a line near the first
 * column can still reach into it, so it is pushed right and anchored at the
 * column's edge instead. The reservation is the WRAP column, not the measured
 * text, so the boundary is the same for every lane. One rule, read by the
 * claim phase and the draw phase alike, so the interval packed is the interval
 * drawn. */
const LANE_LABEL_RIGHT = G.LANE_LABEL_X + G.LANE_LABEL_W
function placeLabel(centre: number, w: number): { x: number; anchor: LabelBox['anchor'] } {
  return centre - w / 2 < LANE_LABEL_RIGHT ? { x: LANE_LABEL_RIGHT, anchor: 'start' } : { x: centre, anchor: 'middle' }
}
/** The x-interval a label claims, wherever `placeLabel` puts it. */
function labelSpan(text: string, centre: number): [number, number] {
  const w = estimateWidth(text)
  const p = placeLabel(centre, w)
  const left = p.anchor === 'start' ? p.x : p.x - w / 2
  return [left - LABEL_MARGIN, left + w + LABEL_MARGIN]
}

function roundedPath(pts: Point[], corner = CORNER): string {
  if (pts.length < 3) return `M ${fmt(pts[0].x)} ${fmt(pts[0].y)} L ${fmt(pts[1].x)} ${fmt(pts[1].y)}`
  const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y)
  const towards = (from: Point, to: Point, r: number): Point => {
    const d = dist(from, to) || 1
    return { x: from.x + ((to.x - from.x) / d) * r, y: from.y + ((to.y - from.y) / d) * r }
  }
  let d = `M ${fmt(pts[0].x)} ${fmt(pts[0].y)}`
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i - 1], c = pts[i], n = pts[i + 1]
    const r = Math.min(corner, dist(p, c) / 2, dist(c, n) / 2)
    const a = towards(c, p, r), b = towards(c, n, r)
    d += ` L ${fmt(a.x)} ${fmt(a.y)} Q ${fmt(c.x)} ${fmt(c.y)} ${fmt(b.x)} ${fmt(b.y)}`
  }
  const last = pts[pts.length - 1]
  return `${d} L ${fmt(last.x)} ${fmt(last.y)}`
}

/** The column gap a long route's vertical leg runs in (spec §3.4): beside
 * the source column on the target's side. Always between two columns. */
function gapFor(s: Cell, t: Cell, columns: number): number {
  if (t.col > s.col) return s.col + 1
  if (t.col < s.col) return s.col
  return s.col === columns - 1 ? s.col : s.col + 1
}

export function layoutProcessMap(vm: ProcessOverviewVM, options: { columns: number }): MapLayout {
  const placement = placeCells(vm, options.columns)
  const { cells, columns } = placement
  const { edges, stubs } = classify(vm, placement)
  const R = placement.rowCount

  // ── 1. long routes: a vertical leg claims a track in a column gap ─────────
  const legOf = new Map<string, { gap: number; lo: number; hi: number }>()
  for (const e of edges) {
    if (e.kind !== 'route') continue
    const s = cells.get(e.from)!, t = cells.get(e.to)!
    if (Math.abs(t.globalRow - s.globalRow) < 2) continue
    legOf.set(e.key, { gap: gapFor(s, t, columns), lo: Math.min(s.globalRow, t.globalRow) + 1, hi: Math.max(s.globalRow, t.globalRow) + 1 })
  }
  const orderOf = new Map<string, string>(edges.map((e) => [e.key, e.order]))
  const gapTracks: Map<string, number>[] = []
  const gapCount: number[] = []
  for (let g = 0; g <= columns; g++) {
    const items: Interval[] = [...legOf.entries()].filter(([, l]) => l.gap === g).map(([id, l]) => ({ id, lo: l.lo, hi: l.hi, order: orderOf.get(id)! }))
    const packed = packIntervals(items)
    gapTracks[g] = packed.track
    gapCount[g] = packed.count
  }
  const gapWidth = (g: number) => G.COL_GAP + Math.max(0, gapCount[g] - G.VTRACK_MAX) * G.VTRACK
  const cardX = (col: number) => {
    let x = G.X0
    for (let c = 0; c < col; c++) x += G.CARD_W + gapWidth(c + 1)
    return x
  }
  const legX = (key: string) => {
    const l = legOf.get(key)!
    const left = cardX(l.gap - 1) + G.CARD_W
    const centre = left + gapWidth(l.gap) / 2
    return centre + (gapTracks[l.gap].get(key)! - (gapCount[l.gap] - 1) / 2) * G.VTRACK
  }
  const baseWidth = cardX(columns - 1) + G.CARD_W + G.PAD_RIGHT

  // ── 2. ports (spec §3.3) ─────────────────────────────────────────────────
  const attachments = new Map<string, Attachment[]>()
  const centreX = (c: Cell) => cardX(c.col) + G.CARD_W / 2
  const attach = (step: string, side: Side, key: string, partnerX: number) => {
    const k = `${step}|${side}`
    attachments.set(k, [...(attachments.get(k) ?? []), { key, partnerX }])
  }
  const isDown = (e: RawEdge) => cells.get(e.to)!.globalRow > cells.get(e.from)!.globalRow
  for (const e of edges) {
    const s = cells.get(e.from)!, t = cells.get(e.to)!
    if (e.kind === 'straight') {
      attach(e.from, s.dir === 1 ? 'right' : 'left', e.key, centreX(t))
      attach(e.to, s.dir === 1 ? 'left' : 'right', e.key, centreX(s))
    } else if (e.kind === 'arc') {
      attach(e.from, 'top', e.key, centreX(t))
      attach(e.to, 'top', e.key, centreX(s))
    } else if (e.kind === 'drop' || isDown(e)) {
      attach(e.from, 'bottom', e.key, centreX(t))
      attach(e.to, 'top', e.key, centreX(s))
    } else {
      attach(e.from, 'top', e.key, centreX(t))
      attach(e.to, 'bottom', e.key, centreX(s))
    }
  }
  /** The segment a `straight` edge draws: out of the source's side edge, to
   * ARROW_GAP short of the target's. ONE expression, so the fit test in the
   * claim phase and the draw phase below measure the same span. */
  const straightSpan = (s: Cell, t: Cell): [number, number] => (s.dir === 1
    ? [cardX(s.col) + G.CARD_W, cardX(t.col) - ARROW_GAP]
    : [cardX(s.col), cardX(t.col) + G.CARD_W + ARROW_GAP])
  const hasStub = new Set(stubs.map((s) => s.step))
  const portX = (step: string, side: Side, key: string): number => {
    const x = cardX(cells.get(step)!.col)
    if (side === 'left') return x
    if (side === 'right') return x + G.CARD_W
    const list = [...(attachments.get(`${step}|${side}`) ?? [])].sort((a, b) => a.partnerX - b.partnerX || (a.key < b.key ? -1 : 1))
    const n = list.length
    const i = list.findIndex((a) => a.key === key)
    // A card with a stub keeps the centre of its bottom edge for it: lay the
    // edges over one slot more and skip the slot nearest the centre.
    const reserve = side === 'bottom' && hasStub.has(step)
    const slots = reserve ? n + 1 : n
    const slot = reserve && i >= Math.floor(n / 2) ? i + 1 : i
    if (slots <= 1) return x + G.CARD_W / 2
    return x + G.PORT_INSET + (slot * (G.CARD_W - 2 * G.PORT_INSET)) / (slots - 1)
  }

  // ── 3. stubs: centred, wrapped (spec §3.5) ───────────────────────────────
  const stubBlocks = stubs.map((st) => ({ st, px: centreX(cells.get(st.step)!), wrapped: st.lines.flatMap((l) => wrapText(l.text, G.STUB_WRAP).map((text) => ({ text, exit: l.exit }))) }))
  const stubHeight = (b: (typeof stubBlocks)[number]) => G.STUB_HEAD + b.wrapped.length * G.STUB_LINE + 2

  // ── 4. channel zones: every item claims an interval (spec §3.5) ──────────
  const arcItems: Interval[][] = Array.from({ length: R + 1 }, () => [])
  const trackItems: Interval[][] = Array.from({ length: R + 1 }, () => [])
  const runsOf = new Map<string, Run[]>()
  const withLabel = (lo: number, hi: number, label: string | null, at: number): [number, number] => {
    if (!label) return [lo, hi]
    const [left, right] = labelSpan(label, at)
    return [Math.min(lo, left), Math.max(hi, right)]
  }
  for (const e of edges) {
    const s = cells.get(e.from)!, t = cells.get(e.to)!
    if (e.kind === 'straight') {
      if (!e.label) continue
      const [x1, x2] = straightSpan(s, t)
      const w = estimateWidth(e.label)
      if (w > Math.abs(x2 - x1) - LABEL_CLEAR) {
        const [lo, hi] = labelSpan(e.label, (x1 + x2) / 2)
        arcItems[s.globalRow].push({ id: `${e.key}#label`, lo, hi, order: e.order })
      }
    } else if (e.kind === 'arc') {
      const sx = portX(e.from, 'top', e.key), tx = portX(e.to, 'top', e.key)
      const [lo, hi] = withLabel(Math.min(sx, tx), Math.max(sx, tx), e.label, (sx + tx) / 2)
      arcItems[s.globalRow].push({ id: e.key, lo, hi, order: e.order })
    } else if (e.kind === 'drop') {
      // SA-454: the drop LANDS on the top port it reserves on its target. Under
      // the snake the two ports coincide and it is the straight vertical it has
      // always been, claiming only its label; when they differ — another line
      // also arrives at the target's top edge — it bends through the channel
      // below the source row and claims a run there, exactly as a route does.
      // The alternative, not reserving the port, is worse: a drop and a lone
      // arriving arc would both compute to the target's centre slot and coincide.
      const px = portX(e.from, 'bottom', e.key), tx = portX(e.to, 'top', e.key)
      const k = s.globalRow + 1
      if (px !== tx) {
        runsOf.set(e.key, [{ channel: k, x1: px, x2: tx }])
        const [lo, hi] = withLabel(Math.min(px, tx), Math.max(px, tx), e.label, (px + tx) / 2)
        trackItems[k].push({ id: `${e.key}#0`, lo, hi, order: e.order })
      } else if (e.label) {
        const w = estimateWidth(e.label)
        trackItems[k].push({ id: `${e.key}#label`, lo: px + LABEL_OFFSET - LABEL_MARGIN, hi: px + LABEL_OFFSET + w + LABEL_MARGIN, order: e.order })
      }
    } else {
      const down = isDown(e)
      const sx = portX(e.from, down ? 'bottom' : 'top', e.key), tx = portX(e.to, down ? 'top' : 'bottom', e.key)
      const runs: Run[] = Math.abs(t.globalRow - s.globalRow) < 2
        ? [{ channel: down ? t.globalRow : s.globalRow, x1: sx, x2: tx }]
        : [
            { channel: down ? s.globalRow + 1 : s.globalRow, x1: sx, x2: legX(e.key) },
            { channel: down ? t.globalRow : t.globalRow + 1, x1: legX(e.key), x2: tx },
          ]
      runsOf.set(e.key, runs)
      runs.forEach((r, i) => {
        let lo = Math.min(r.x1, r.x2), hi = Math.max(r.x1, r.x2)
        if (i === 0 && e.label) {
          if (r.x1 === r.x2) {
            const w = estimateWidth(e.label)
            lo = Math.min(lo, r.x1 + LABEL_OFFSET - LABEL_MARGIN)
            hi = Math.max(hi, r.x1 + LABEL_OFFSET + w + LABEL_MARGIN)
          } else [lo, hi] = withLabel(lo, hi, e.label, (r.x1 + r.x2) / 2)
        }
        trackItems[r.channel].push({ id: `${e.key}#${i}`, lo, hi, order: e.order })
      })
    }
  }
  const arcPacked = arcItems.map(packIntervals)
  const trackPacked = trackItems.map(packIntervals)

  // ── 5. heights and y positions (spec §3.5, the channel budget) ────────────
  const stubsAbove = (k: number) => stubBlocks.filter((b) => cells.get(b.st.step)!.globalRow === k - 1)
  const stubZone = (k: number) => {
    const hs = stubsAbove(k).map(stubHeight)
    return hs.length ? Math.max(...hs) + G.ZONE_PAD : 0
  }
  const trackZone = (k: number) => (trackPacked[k].count ? trackPacked[k].count * G.TRACK + G.ZONE_PAD : 0)
  const arcZone = (k: number) => (arcPacked[k].count ? arcPacked[k].count * G.ARC_LEVEL + G.ZONE_PAD : 0)
  const channelH = (k: number) => Math.max(G.CHANNEL_MIN, (stubZone(k) || G.ZONE_PAD) + BAND_GAP + trackZone(k) + BAND_GAP + (arcZone(k) || G.ZONE_PAD))
  const channelTop: number[] = []
  const rowTop: number[] = []
  let y = 0
  for (let k = 0; k < R; k++) {
    channelTop[k] = y
    y += channelH(k)
    rowTop[k] = y
    y += G.CARD_H
  }
  channelTop[R] = y
  const height = y + channelH(R) + G.PAD_BOTTOM
  const trackY = (k: number, j: number) => channelTop[k] + (stubZone(k) || G.ZONE_PAD) + BAND_GAP + (j + 1) * G.TRACK - 2
  const levelY = (k: number, L: number) => rowTop[k] - G.ZONE_PAD - (L + 1) * G.ARC_LEVEL + 4

  // ── 6. output ────────────────────────────────────────────────────────────
  const cards: CardBox[] = vm.steps.map((s) => {
    const c = cells.get(s.id)!
    return { id: s.id, x: cardX(c.col), y: rowTop[c.globalRow], w: G.CARD_W, h: G.CARD_H, lane: c.lane, row: c.row, col: c.col, globalRow: c.globalRow }
  })
  const cardOf = new Map(cards.map((c) => [c.id, c]))
  const label = (text: string, x: number, lineY: number, anchor: LabelBox['anchor']): LabelBox =>
    ({ text, x: fmt(x), y: fmt(lineY + 4), anchor, w: estimateWidth(text), h: G.LABEL_H })
  /** Centred on its line, unless that would enter the lane-label column. */
  const centred = (text: string, centre: number, lineY: number): LabelBox => {
    const p = placeLabel(centre, estimateWidth(text))
    return label(text, p.x, lineY, p.anchor)
  }

  const out: EdgePath[] = edges.map((e) => {
    const s = cells.get(e.from)!
    const sc = cardOf.get(e.from)!, tc = cardOf.get(e.to)!
    let points: Point[]
    let lbl: LabelBox | null = null
    if (e.kind === 'straight') {
      const [x1, x2] = straightSpan(s, cells.get(e.to)!)
      const ly = sc.y + G.CARD_H / 2
      points = [{ x: x1, y: ly }, { x: x2, y: ly }]
      if (e.label) {
        const L = arcPacked[s.globalRow].track.get(`${e.key}#label`)
        lbl = centred(e.label, (x1 + x2) / 2, L === undefined ? ly : levelY(s.globalRow, L))
      }
    } else if (e.kind === 'arc') {
      const sx = portX(e.from, 'top', e.key), tx = portX(e.to, 'top', e.key)
      const ly = levelY(s.globalRow, arcPacked[s.globalRow].track.get(e.key)!)
      points = [{ x: sx, y: sc.y }, { x: sx, y: ly }, { x: tx, y: ly }, { x: tx, y: tc.y - ARROW_GAP }]
      if (e.label) lbl = centred(e.label, (sx + tx) / 2, ly)
    } else if (e.kind === 'drop') {
      const px = portX(e.from, 'bottom', e.key), tx = portX(e.to, 'top', e.key)
      const k = s.globalRow + 1
      if (px === tx) {
        points = [{ x: px, y: sc.y + G.CARD_H }, { x: px, y: tc.y - ARROW_GAP }]
        if (e.label) lbl = label(e.label, px + LABEL_OFFSET, trackY(k, trackPacked[k].track.get(`${e.key}#label`)!), 'start')
      } else {
        const ry = trackY(k, trackPacked[k].track.get(`${e.key}#0`)!)
        points = [{ x: px, y: sc.y + G.CARD_H }, { x: px, y: ry }, { x: tx, y: ry }, { x: tx, y: tc.y - ARROW_GAP }]
        if (e.label) lbl = centred(e.label, (px + tx) / 2, ry)
      }
    } else {
      const down = isDown(e)
      const sx = portX(e.from, down ? 'bottom' : 'top', e.key), tx = portX(e.to, down ? 'top' : 'bottom', e.key)
      const sy = down ? sc.y + G.CARD_H : sc.y
      const ty = down ? tc.y - ARROW_GAP : tc.y + G.CARD_H + ARROW_GAP
      const runs = runsOf.get(e.key)!
      const runY = (i: number) => trackY(runs[i].channel, trackPacked[runs[i].channel].track.get(`${e.key}#${i}`)!)
      if (runs.length === 1) {
        const ry = runY(0)
        points = [{ x: sx, y: sy }, { x: sx, y: ry }, { x: tx, y: ry }, { x: tx, y: ty }]
        if (e.label) lbl = sx === tx ? label(e.label, sx + LABEL_OFFSET, ry, 'start') : centred(e.label, (sx + tx) / 2, ry)
      } else {
        const gx = legX(e.key), r1 = runY(0), r2 = runY(1)
        points = [{ x: sx, y: sy }, { x: sx, y: r1 }, { x: gx, y: r1 }, { x: gx, y: r2 }, { x: tx, y: r2 }, { x: tx, y: ty }]
        if (e.label) lbl = sx === gx ? label(e.label, sx + LABEL_OFFSET, r1, 'start') : centred(e.label, (sx + gx) / 2, r1)
      }
    }
    points = points.map((p) => ({ x: fmt(p.x), y: fmt(p.y) }))
    return { key: e.key, from: e.from, to: e.to, exit: e.exit, kind: e.kind, back: e.back, points, d: roundedPath(points, e.kind === 'arc' ? ARC_CORNER : CORNER), label: lbl }
  })

  const stubOut: StubBlock[] = stubBlocks.map((b) => {
    const c = cardOf.get(b.st.step)!
    const y0 = c.y + G.CARD_H
    return {
      step: b.st.step, x: b.px, y: y0,
      arrowD: `M ${fmt(b.px)} ${y0} L ${fmt(b.px)} ${y0 + G.STUB_HEAD - 3}`,
      lines: b.st.lines,
      labels: b.wrapped.map(({ text, exit }, i) => ({ ...centred(text, b.px, y0 + G.STUB_HEAD + LABEL_OFFSET + i * G.STUB_LINE), exit })),
    }
  })

  // SA-454: the map is as wide as its widest DRAWN thing, not as its last
  // card. A label's box is its real extent whatever its anchor — a `drop`
  // label (and a zero-width route's) sits to the RIGHT of its line, and an
  // even row always wraps in the LAST column — so the base width alone lets
  // the viewport's overflow clip an exit name at fit-to-width.
  const allLabels: LabelBox[] = [...out.flatMap((e) => (e.label ? [e.label] : [])), ...stubOut.flatMap((b) => b.labels)]
  const width = allLabels.reduce((w, b) => Math.max(w, labelRect(b).x + b.w + G.PAD_RIGHT), baseWidth)

  const lanes: LaneBox[] = placement.lanes.map((l) => {
    const first = l.firstGlobalRow, last = l.firstGlobalRow + l.rows - 1
    const top = rowTop[first] - (arcZone(first) || G.ZONE_PAD) - BAND_REACH
    const bottom = rowTop[last] + G.CARD_H + (stubZone(last + 1) || G.ZONE_PAD) + BAND_REACH
    const labelLines = wrapText(l.label, Math.floor(G.LANE_LABEL_W / G.CHAR_W))
    const laneLabel = (text: string, baseline: number): LabelBox =>
      ({ text, x: G.LANE_LABEL_X, y: fmt(baseline), anchor: 'start', w: estimateWidth(text), h: G.LABEL_H })
    const labels = [
      ...labelLines.map((line, i) => laneLabel(line, top + G.LANE_LABEL_TOP + i * G.LANE_LABEL_LINE)),
      laneLabel(`${l.count} steps`, top + G.LANE_LABEL_TOP + labelLines.length * G.LANE_LABEL_LINE + G.LANE_COUNT_GAP),
    ]
    return { id: l.id, label: l.label, labelLines, count: l.count, x: 0, y: top, w: width, h: bottom - top, labels }
  })

  return { width, height, columns, lanes, cards, edges: out, stubs: stubOut }
}
